import {
  Grid,
  LinearProgress,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Button,
  Skeleton,
  Stack,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Box from "@mui/material/Box";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Reports } from "../../../../../app/slices/reportsSlice";
import { SelectRepresentative } from "../../../../../components/Selectors/SelectRepresentative";
import { HiOutlineUserCircle } from "react-icons/hi2";

// Brand palette (Dejlah primary blue)
const BLUE = "#0C86B6";

// Clickable cash value rendered as a button-style pill so it stays easy to
// see and tap even when the amount is zero.
const CashButton = ({ id, cash }) => (
  <Button
    onClick={() => {
      localStorage.setItem("isWholeSale", "0");
    }}
    href={`/app/wallets/transactions/${id}`}
    target="_blank"
    rel="noopener noreferrer"
    variant="outlined"
    size="small"
    sx={{
      minWidth: 64,
      py: 0.25,
      px: 1.25,
      borderColor: "rgba(12,134,182,0.35)",
      color: BLUE,
      fontWeight: 700,
      lineHeight: 1.4,
      textTransform: "none",
      "&:hover": {
        borderColor: "rgba(12,134,182,0.55)",
        bgcolor: "rgba(12,134,182,0.08)",
      },
    }}
  >
    {(cash ?? 0).toLocaleString()}
  </Button>
);

// A single label/value row inside a rep card.
const InfoRow = ({ label, value }) => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    spacing={2}
    sx={{ py: 1 }}
  >
    <Typography sx={{ fontSize: 13.5, color: "#667085" }}>{label}</Typography>
    <Box sx={{ fontSize: 14, fontWeight: 700, color: "#1f2a37" }}>{value}</Box>
  </Stack>
);

// Modern rep card matching the redesigned form-dialog design language.
const RepCard = ({ children }) => (
  <Box
    sx={{
      bgcolor: "#ffffff",
      border: "1px solid #edf0f3",
      borderRadius: 3,
      p: { xs: 2, sm: 2.5 },
      mb: 1.5,
      boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    }}
  >
    {children}
  </Box>
);

const RepDetailsTabel = () => {
  const dispatch = useDispatch();

  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const _RepDetails = useSelector((state) => state.reports.repDetails);
  const loading = useSelector((state) => state.reports.reports.loading);
  const _rep = useSelector((state) => state.user.usersList.data);

  const [search, setSearch] = useState({
    is_client: false,
    rep_id: null,
  });

  const columns = [
    {
      field: "name",
      headerName: "الاسم",
      flex: 1,
    },
    {
      field: "mobile",
      headerName: "رقم الهاتف",
      flex: 1,
    },
    {
      field: "cash",
      headerName: "الكاش (IQD)",
      flex: 1,
      renderCell: (params) => (
        <CashButton id={params.row.id} cash={params.row?.cash} />
      ),
    },
    {
      field: "balance",
      headerName: "الرصيد (IQD)",
      flex: 1,
      renderCell: (params) => {
        return <>{params.row?.balance?.toLocaleString()}</>;
      },
    },
    {
      field: "debt",
      headerName: "الديون (IQD)",
      flex: 1,
      renderCell: (params) => {
        return <>{params.row?.debt?.toLocaleString()}</>;
      },
    },
    {
      field: "total_target",
      headerName: "المبيعات هذا الشهر (IQD)",
      flex: 1,
      renderCell: (params) => {
        return <>{params.row?.total_target?.toLocaleString()}</>;
      },
    },
    {
      field: "rep_pos_count",
      headerName: "عدد نقاط البيع",
      flex: 1,
    },
    {
      field: "total_pos_balance",
      headerName: "رصيد النقاط (IQD)",
      flex: 1,
      renderCell: (params) => params.row?.total_pos_balance?.toLocaleString(),
    },
    {
      field: "total_pos_debt",
      headerName: "ديون النقاط (IQD)",
      flex: 1,
      renderCell: (params) => params.row?.total_pos_debt?.toLocaleString(),
    },
    // {
    //   field: "last_transaction",
    //   headerName: "رقم آخر معاملة",
    //   flex: 1,
    //   valueGetter: (params) => params.row.last_transaction ? params.row.last_transaction.number : "",
    //   renderCell: (params) => {
    //     return (
    //       <>
    //         {params.row.last_transaction ? params.row.last_transaction.number : "---"}
    //       </>
    //     );
    //   },
    // },
    // {
    //   field: "created_at",
    //   headerName: "تاريخ آخر معاملة",
    //   flex: 1,
    //   valueGetter: (params) => params.row.last_transaction ? dayjs(params.row.last_transaction.created_at).format(" YYYY-MM-DD / MM:hha ") : "",
    //   renderCell: (params) => {
    //     return (
    //       <>
    //         {params.row.last_transaction ? dayjs(params.row.last_transaction.created_at).format(" YYYY-MM-DD / MM:hha ") : "---"}
    //       </>
    //     );
    //   },
    // },
  ];

  const handleSearch = () => {
    dispatch(
      Reports.getAll({
        is_client: search.is_client,
        rep_id: search.rep_id ?? "",
      }),
    );
  };

  const customToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarExport
        csvOptions={{
          fileName: "تقرير تفاصيل المندوب | نظام دجلة",
          utf8WithBom: true,
        }}
      />
    </GridToolbarContainer>
  );

  return (
    <Card>
      <CardContent>
        <Grid container rowSpacing={1} columnSpacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <SelectRepresentative
              onChange={(event, value) => {
                setSearch({
                  ...search,
                  rep_id: value ? value.id : null,
                });
              }}
              value={_rep?.filter((x) => x.id === search.rep_id)[0] ?? null}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Switch
                  checked={search.is_client === 1 ? true : false}
                  onChange={(e) =>
                    setSearch({
                      ...search,
                      is_client: e.target.checked ? 1 : 0,
                    })
                  }
                />
              }
              label="العملاء"
            />
            <Button variant="contained" color="primary" onClick={handleSearch}>
              بحث
            </Button>
          </Grid>
          {smallScreen ? (
            loading ? (
              <Grid item xs={12}>
                {Array.from(new Array(5)).map((_, index) => (
                  <RepCard key={index}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ mb: 1.5 }}
                    >
                      <Skeleton variant="rounded" width={44} height={44} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="55%" height={26} />
                        <Skeleton variant="text" width="35%" height={18} />
                      </Box>
                    </Stack>
                    <Divider sx={{ borderColor: "#edf0f3" }} />
                    {Array.from(new Array(7)).map((__, i) => (
                      <Stack
                        key={i}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ py: 1 }}
                      >
                        <Skeleton variant="text" width="30%" height={20} />
                        <Skeleton variant="text" width="25%" height={20} />
                      </Stack>
                    ))}
                  </RepCard>
                ))}
              </Grid>
            ) : _RepDetails?.data?.length > 0 ? (
              <Grid item xs={12}>
                {_RepDetails.data.map((data, index) => (
                  <RepCard key={data.id ?? index}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ mb: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "12px",
                          display: "grid",
                          placeItems: "center",
                          bgcolor: "rgba(12,134,182,0.12)",
                          color: BLUE,
                          fontSize: 24,
                          flexShrink: 0,
                        }}
                      >
                        <HiOutlineUserCircle />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: 16,
                            color: "#1f2a37",
                            lineHeight: 1.3,
                          }}
                          noWrap
                        >
                          {data?.name}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "#667085" }}>
                          {data?.mobile || "—"}
                        </Typography>
                      </Box>
                    </Stack>

                    <Divider sx={{ borderColor: "#edf0f3" }} />

                    <InfoRow
                      label="الكاش (IQD)"
                      value={<CashButton id={data.id} cash={data?.cash} />}
                    />
                    <Divider sx={{ borderColor: "#f2f4f7" }} />
                    <InfoRow
                      label="الرصيد (IQD)"
                      value={data?.balance?.toLocaleString()}
                    />
                    <Divider sx={{ borderColor: "#f2f4f7" }} />
                    <InfoRow
                      label="الديون (IQD)"
                      value={data?.debt?.toLocaleString()}
                    />
                    <Divider sx={{ borderColor: "#f2f4f7" }} />
                    <InfoRow
                      label="المبيعات هذا الشهر (IQD)"
                      value={data?.total_target?.toLocaleString()}
                    />
                    <Divider sx={{ borderColor: "#f2f4f7" }} />
                    <InfoRow
                      label="عدد نقاط البيع"
                      value={data?.rep_pos_count}
                    />
                    <Divider sx={{ borderColor: "#f2f4f7" }} />
                    <InfoRow
                      label="رصيد النقاط (IQD)"
                      value={data?.total_pos_balance?.toLocaleString()}
                    />
                    <Divider sx={{ borderColor: "#f2f4f7" }} />
                    <InfoRow
                      label="ديون النقاط (IQD)"
                      value={data?.total_pos_debt?.toLocaleString()}
                    />
                  </RepCard>
                ))}
              </Grid>
            ) : (
              <Grid item xs={12}>
                <RepCard>
                  <Typography
                    variant="h5"
                    align="center"
                    sx={{ color: "#667085", py: 2 }}
                  >
                    لا توجد بيانات
                  </Typography>
                </RepCard>
              </Grid>
            )
          ) : (
            <Grid item xs={12}>
              <Box sx={{ height: "70vh", width: "100%" }}>
                {_RepDetails.data ? (
                  <DataGrid
                    rows={_RepDetails.data}
                    columns={columns}
                    rowCount={_RepDetails.total}
                    loading={loading}
                    rowHeight={40}
                    selectionModel={[]}
                    rowsPerPageOptions={[1000]}
                    components={{
                      LoadingOverlay: LinearProgress,
                      Toolbar: customToolbar,
                    }}
                  />
                ) : (
                  <LinearProgress />
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default RepDetailsTabel;
